// Top-level navigation sidebar.
// Rendered once inside ApiProvider; links push React Router routes.
import React from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useProfile } from '../contexts/ApiContext'
import { useClerk, OrganizationSwitcher } from '@clerk/clerk-react'

function initials(name: string) {
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return (parts[0][0] ?? '?').toUpperCase()
  return ((parts[0][0] ?? '') + (parts[parts.length - 1][0] ?? '')).toUpperCase()
}

// ─── SVG icon helper ──────────────────────────────────────────────────────────

function Icon({ d, d2 }: { d: string; d2?: string }) {
  return (
    <svg
      style={{ width: 15, height: 15, flexShrink: 0 }}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={d} />
      {d2 && <path d={d2} />}
    </svg>
  )
}

// ─── Icon paths (Heroicons outline) ───────────────────────────────────────────

const ICONS = {
  reservations:'M8 7V3m8 4V3M3 11h18M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  schedule: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  channels: 'M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z',
  broadcast: 'M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z',
  handoff: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
  coa: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
  journal: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
  balance: 'M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3',
  fiscal:    'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  vendors:   'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
  products:  'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4',
  receivings:'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  counts:    'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
  transfers: 'M8 7h12m0 0l-4-4m4 4l-4 4m4 6H4m0 0l4 4m-4-4l4-4',
  countaudit:'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  waste:     'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
  dailycount:'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  roles:     'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
  menu:      'M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25',
  pos:       'M9 14.25l6-6m4.5-3.493V21.75l-3.75-1.5-3.75 1.5-3.75-1.5-3.75 1.5V4.757c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0c1.1.128 1.907 1.077 1.907 2.185zM9.75 9h.008v.008H9.75V9zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm4.125 4.5h.008v.008h-.008V13.5zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z',
  kds:       'M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25',
  cash:      'M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z',
} as const

type IconKey = keyof typeof ICONS

// ─── Nav data ─────────────────────────────────────────────────────────────────

interface NavItem  { to: string; iconKey: IconKey; label: string; ownerOnly?: boolean; permission?: string }
interface NavSection { label: string; items: NavItem[]; ownerOnly?: boolean; permission?: string }

const NAV: NavSection[] = [
  {
    // Module 0 · m0-3 — the tenant onboarding wizard. Owner/GM only (server gates GET
    // /api/setup/checklist on roles.manage @ global); the section is permission-gated so it
    // disappears for staff who cannot set the tenant up, and naturally recedes once activated.
    label: 'Get started',
    permission: 'roles.manage',
    items: [
      { to: '/setup', iconKey: 'roles', label: 'Setup Checklist', permission: 'roles.manage' },
    ],
  },
  {
    // Module 9 inc-9-4-web slice 2 — the employee take-surface. Ungated (no
    // permission/ownerOnly) so EVERY signed-in employee sees their own assigned
    // training; the server scopes the data to self and enforces subject-only completion.
    label: 'Personal',
    items: [
      { to: '/my-training', iconKey: 'countaudit', label: 'My Training' },
      // Module 25 · cal-1b — the Management Calendar. Ungated (member-visible): every signed-in
      // member has a personal calendar; the server scopes the agenda to the caller's subtree ∪
      // their own to-dos via resolveOrgScope.
      { to: '/calendar', iconKey: 'schedule', label: 'Calendar' },
      // Module 25 · cal-2b — the Policy Register. Ungated (member-visible): every member sees the
      // company-wide handbook + team policies in their subtree; the server scopes reads via
      // resolveOrgScope, and gates writes on policy.manage.
      { to: '/policies', iconKey: 'coa', label: 'Policies' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/schedule',     iconKey: 'schedule', label: 'Schedule' },
      { to: '/labor/report', iconKey: 'balance',  label: 'Labor vs Sales' },
      { to: '/accounting/cost-vs-sales', iconKey: 'balance', label: 'Cost vs Sales' },
    ],
  },
  {
    // Module 24 — Org & Teams. org-3b: the read-only Org Chart over GET /api/org/chart.
    // Ungated: member-visible ("all Team Members can see structure") — the server scopes the
    // returned teams to the caller's org vantage via resolveOrgScope. org-4b: the Design
    // Canvas (Owner/GM only — permission: team.configure) is the editable surface behind it.
    label: 'Organization',
    items: [
      { to: '/org-chart', iconKey: 'roles', label: 'Org Chart' },
      { to: '/org-canvas', iconKey: 'roles', label: 'Design Canvas', permission: 'team.configure' },
      // DEF-207b-2 — the owner downloads the restaurant's data and every stored file (photos, certification scans).
      { to: '/your-data', iconKey: 'products', label: 'Your Data', ownerOnly: true },
    ],
  },
  {
    // Module 22 apex — the GM Confidant. GM/owner-only (gated on `gm.confidant`, GLOBAL scope);
    // the owner always sees it. gm-1b surfaces the read-only Daily Brief.
    label: 'GM Confidant',
    permission: 'gm.confidant',
    items: [
      { to: '/gm/daily-brief', iconKey: 'balance', label: 'Daily Brief' },
    ],
  },
  {
    label: 'Reports',
    items: [
      { to: '/reports',       iconKey: 'balance', label: 'Overview' },
      { to: '/reports/sales', iconKey: 'balance', label: 'Sales Analytics' },
      { to: '/reports/inventory', iconKey: 'counts', label: 'Inventory Loss' },
      { to: '/reports/prime-cost', iconKey: 'balance', label: 'Prime Cost', permission: 'accounting.view' },
    ],
  },
  {
    label: 'Point of Sale',
    items: [
      { to: '/pos/terminal',   iconKey: 'pos',  label: 'Terminal' },
      { to: '/pos/kds',        iconKey: 'kds',  label: 'Kitchen Display' },
      { to: '/pos/cash-tips',  iconKey: 'cash', label: 'Cash & Tips' },
      { to: '/pos/tip-pool',   iconKey: 'cash', label: 'Tip Pool' },
      { to: '/pos/payments',   iconKey: 'cash', label: 'Card Payments', ownerOnly: true },
      { to: '/pos/printers',   iconKey: 'pos',  label: 'Printers' },
      { to: '/pos/settings',   iconKey: 'pos',  label: 'POS Settings' }, // rb-6: the guest count
    ],
  },
  {
    label: 'Hours',
    items: [
      { to: '/hours-of-operation', iconKey: 'schedule', label: 'Hours of Operation' },
    ],
  },
  {
    label: 'Reservations',
    items: [
      { to: '/reservations', iconKey: 'reservations', label: 'Reservation Book' },
    ],
  },
  {
    // Module 6 inc-6-web-a — Guest directory (CRM). Gated on `crm.view` (defaulted onto every
    // management role); owner always sees it. Manage-only controls (create/edit/archive) are
    // gated in-page on `crm.manage`. (Route/permission keys stay `crm.*` — user-facing label only.)
    label: 'Guests',
    permission: 'crm.view',
    items: [
      { to: '/customers', iconKey: 'roles', label: 'Directory' },
    ],
  },
  {
    label: 'Messaging',
    items: [
      { to: '/messaging/channels',      iconKey: 'channels',   label: 'Channels'      },
      { to: '/messaging/broadcast',     iconKey: 'broadcast',  label: 'Broadcast'     },
      { to: '/messaging/handoff-notes', iconKey: 'handoff',    label: 'Handoff Notes' },
    ],
  },
  {
    label: 'Accounting',
    items: [
      { to: '/cash',                       iconKey: 'cash',    label: 'Cash Management'   },
      { to: '/accounting/accounts',        iconKey: 'coa',     label: 'Chart of Accounts' },
      { to: '/accounting/journal-entries', iconKey: 'journal', label: 'Journal Entries'   },
      { to: '/accounting/trial-balance',   iconKey: 'balance', label: 'Trial Balance'     },
      // DEF-100 S3: post hourly wages + salaries for an operational period (server-gated accounting.post).
      { to: '/accounting/period-close',    iconKey: 'fiscal',  label: 'Period Close'      },
      // DEF-106 M2: staff-meal policy + pay schedule (server-gated staff.manage, global — Owner / all-stations GM).
      { to: '/accounting/payroll-settings', iconKey: 'fiscal', label: 'Payroll Settings', permission: 'staff.manage' },
      // DEF-106 M2: the pay-period staff-meal report — everyone (the server shows each person what they may see).
      { to: '/accounting/staff-meals',     iconKey: 'balance', label: 'Staff Meals'       },
      { to: '/accounting/setup',           iconKey: 'fiscal',  label: 'Fiscal Setup'      },
    ],
  },
  {
    label: 'Inventory',
    items: [
      { to: '/inventory/vendors',    iconKey: 'vendors',    label: 'Vendors'           },
      { to: '/inventory/products',   iconKey: 'products',   label: 'Products'          },
      { to: '/inventory/receivings', iconKey: 'receivings', label: 'Receiving History' },
      { to: '/inventory/counts',       iconKey: 'counts',      label: 'Inventory Counts'   },
      { to: '/inventory/transfers',    iconKey: 'transfers',   label: 'Transfers'          },
      { to: '/inventory/count-audit',  iconKey: 'countaudit',  label: 'Count Audit'        },
      { to: '/inventory/sales-imports', iconKey: 'receivings', label: 'Sales Imports'      },
      { to: '/inventory/ledger-waste', iconKey: 'waste',       label: 'Waste (Ledger)'     },
      { to: '/inventory/waste',        iconKey: 'waste',       label: 'Waste Log'          },
      { to: '/inventory/daily-count',  iconKey: 'dailycount',  label: 'Daily Count Setup'  },
      // Recipe Builder rb-7 — batches of tracked preps; recipes.manage holders reach it from Prep Items too.
      { to: '/recipes/production',     iconKey: 'products',    label: 'Production', permission: 'inventory.produce' },
      // Recipe Builder rb-9 — what to prep, by the day it must start (the same keys as Production).
      { to: '/recipes/prep-plan',      iconKey: 'products',    label: 'Prep Plan',  permission: 'inventory.produce' },
    ],
  },
  {
    label: 'SRL',
    permission: 'srl.view',
    items: [
      { to: '/srl/items',  iconKey: 'products', label: 'SRL Items'  },
      { to: '/srl/counts', iconKey: 'counts',   label: 'SRL Counts' },
    ],
  },
  {
    // Recipe Builder rb-2/rb-3/rb-4/rb-8 — every recipes.manage holder (Owner, GM, Executive Chef, Bar Manager by default).
    label: 'Recipes',
    permission: 'recipes.manage',
    items: [
      { to: '/recipes/menu', iconKey: 'menu',     label: 'Menu Recipes' },
      { to: '/recipes/prep', iconKey: 'products', label: 'Prep Items'   },
      { to: '/recipes/over-target', iconKey: 'cash', label: 'Over Target' },        // rb-4: items whose cost has drifted past their target
      { to: '/recipes/menu-engineering', iconKey: 'balance', label: 'Menu Engineering' }, // rb-8: popularity × margin, per menu section
      { to: '/recipes/q-factor', iconKey: 'balance', label: 'Q-Factor' },           // rb-5: the measured Q-factor; the Owner or GM marks the products
      { to: '/recipes/actual-vs-theoretical', iconKey: 'balance', label: 'Actual vs Theoretical' }, // rb-10b: usage vs what sales say, unexplained by class
      { to: '/recipes/pricing', iconKey: 'balance', label: 'Pricing Targets' }, // rb-3: read by the chef, set by the Owner or GM
    ],
  },
  {
    label: 'Menu',
    ownerOnly: true,
    items: [
      { to: '/menu/items', iconKey: 'menu', label: 'Menu Items' },
    ],
  },
  {
    // inc-4b: Team Roles (assignment) is open to department managers holding
    // `staff.manage`; Role Setup (role create/edit) stays OWNER-only.
    label: 'Team',
    permission: 'staff.manage',
    items: [
      { to: '/hr/staff',      iconKey: 'roles', label: 'Staff' },
      { to: '/hr/roles',      iconKey: 'roles', label: 'Roles' },
      { to: '/hr/role-setup', iconKey: 'roles', label: 'Role Setup', ownerOnly: true },
    ],
  },
  {
    // Module 9 inc-9-1d — Training & Compliance. Shown to `training.view` holders
    // (the management roles by seed). Compliance dashboard lands in inc-9-1d-ii.
    label: 'Training',
    permission: 'training.view',
    items: [
      { to: '/training/library',        iconKey: 'menu',       label: 'Library' },
      { to: '/training/compliance',     iconKey: 'balance',    label: 'Compliance' },
      { to: '/training/onboarding',     iconKey: 'handoff',    label: 'Onboarding' },
      { to: '/training/certifications', iconKey: 'countaudit', label: 'Certifications' },
      // Module 9 inc-9-3 — Performance & HR records. A finer sub-gate: only records.view
      // holders (Owner + General Manager by seed) see the link; a dept cert-manager
      // (training.view but not records.*) does not.
      { to: '/training/records',        iconKey: 'roles',      label: 'Records', permission: 'training.records.view' },
    ],
  },
]

// ─── Component ────────────────────────────────────────────────────────────────

export function AppNav() {
  const profile  = useProfile()
  const { signOut } = useClerk()
  const navigate = useNavigate()

  const perms = profile.permissions ?? []
  const isOwner = profile.role === 'OWNER'
  const canSee = (n: { ownerOnly?: boolean; permission?: string }) =>
    (!n.ownerOnly || isOwner) && (!n.permission || isOwner || perms.includes(n.permission))

  const name = profile.actingAsPlatformAdmin
    ? 'TOC Admin' // DEF-80c — a platform admin acting inside this restaurant
    : profile.employee
      ? `${profile.employee.firstName} ${profile.employee.lastName}`
      : profile.clerkUserId.slice(0, 8)

  return (
    <nav style={styles.nav} aria-label="Main navigation">
      {/* Logo / restaurant name */}
      <div style={styles.brand}>
        <div style={styles.logoMark}>
          <span style={styles.logoText}>RS</span>
        </div>
        <div style={styles.brandText}>
          <span style={styles.brandName}>RestaurantSuite</span>
          <span style={styles.brandSub}>{profile.restaurant.name}</span>
        </div>
      </div>

      {/* Nav sections */}
      <div style={styles.sections}>
        {NAV.filter(canSee).map(section => (
          <div key={section.label} style={styles.section}>
            <span style={styles.sectionLabel}>{section.label}</span>
            {section.items.filter(canSee).map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                style={({ isActive }) => ({
                  ...styles.navItem,
                  ...(isActive ? styles.navItemActive : {}),
                })}
              >
                <span style={styles.navIcon}>
                  <Icon d={ICONS[item.iconKey]} />
                </span>
                {item.label}
              </NavLink>
            ))}
          </div>
        ))}
      </div>

      {/* Organization switcher — how an owner changes location, and how TOC platform
          staff flip their active org to the "Staff" org to enter read-only support mode. */}
      <div style={styles.orgSwitch}>
        <OrganizationSwitcher
          hidePersonal
          afterSelectOrganizationUrl="/"
          appearance={{
            elements: {
              rootBox: { width: '100%' },
              organizationSwitcherTrigger: { width: '100%', justifyContent: 'flex-start' },
            },
          }}
        />
      </div>

      {/* User / sign out */}
      <div style={styles.footer}>
        <div style={styles.userRow}>
          <div style={styles.avatar}>{initials(name)}</div>
          <div style={styles.userInfo}>
            <span style={styles.userName}>{name}</span>
            <span style={styles.userRole}>{profile.role.charAt(0) + profile.role.slice(1).toLowerCase()}</span>
          </div>
        </div>
        <button
          style={styles.signOutBtn}
          onClick={() => signOut(() => navigate('/sign-in'))}
        >
          Sign out
        </button>
      </div>
    </nav>
  )
}

const styles: Record<string, React.CSSProperties> = {
  nav: {
    width: 220,
    flexShrink: 0,
    height: '100vh',
    background: 'var(--color-surface)',
    borderRight: '1px solid var(--color-border)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '16px 16px 12px',
    borderBottom: '1px solid var(--color-border)',
  },
  logoMark: {
    width: 30,
    height: 30,
    borderRadius: 8,
    background: 'var(--color-primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  logoText:   { fontSize: 11, fontWeight: 700, color: '#fff', letterSpacing: '0.02em' },
  brandText:  { display: 'flex', flexDirection: 'column', minWidth: 0, gap: 1 },
  brandName:  { fontSize: 13, fontWeight: 600, color: 'var(--color-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  brandSub:   { fontSize: 11, color: 'var(--color-text-subtle)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  sections:   { flex: 1, overflowY: 'auto', padding: '8px 0' },
  section:    { marginBottom: 16, padding: '0 8px' },
  sectionLabel: {
    display: 'block',
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    color: 'var(--color-text-subtle)',
    padding: '4px 8px 6px',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '7px 10px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--color-text-muted)',
    textDecoration: 'none',
    marginBottom: 2,
  },
  navItemActive: {
    background: 'var(--color-primary-light)',
    color: 'var(--color-primary)',
    fontWeight: 600,
    boxShadow: 'inset 3px 0 0 var(--color-primary)',
  },
  navIcon: {
    width: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  orgSwitch: {
    padding: '10px 12px',
    borderTop: '1px solid var(--color-border)',
  },
  footer: {
    padding: '12px 16px',
    borderTop: '1px solid var(--color-border)',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  userRow:    { display: 'flex', alignItems: 'center', gap: 8 },
  avatar:     { width: 28, height: 28, borderRadius: '50%', background: 'var(--color-primary)', color: '#fff', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  userInfo:   { display: 'flex', flexDirection: 'column', minWidth: 0 },
  userName:   { fontSize: 12, fontWeight: 600, color: 'var(--color-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  userRole:   { fontSize: 10, color: 'var(--color-text-subtle)', textTransform: 'capitalize' },
  signOutBtn: { background: 'none', border: 'none', fontSize: 12, color: 'var(--color-text-muted)', cursor: 'pointer', textAlign: 'left', padding: 0 },
}
